<template>
  <div class="masthead" :class="classes">
    <div class="grid grid--container">
      <div class="masthead__content">
        <VText v-if="subtitle" class="masthead__for" component="p" variant="h5">
          {{ subtitle }}</VText
        >
        <VText class="masthead__title" variant="h1">{{ title }}</VText>
        <VText class="masthead__text">{{ text }}</VText>
        <div class="masthead__cta">
          <div class="masthead__cta__actions">
            <slot />
          </div>
          <template v-if="ctaText">
            <div />
            <VText class="masthead__btn-text">{{ ctaText }}</VText>
          </template>
        </div>
      </div>

      <transition-group
        v-if="profileImages"
        name="previews"
        tag="div"
        class="masthead__profiles"
        ref="profilesContainer"
        appear
      >
        <template>
          <div
            class="masthead__profile"
            v-for="src in usernamesToShow"
            :key="src"
          >
            <DeviceIphone>
              <nuxt-img
                v-if="isClient"
                :src="src"
                loading="lazy"
                :sizes="`xs:300px`"
                style="width: 100%; height: 100%"
              />
            </DeviceIphone>
          </div>
        </template>
      </transition-group>
    </div>
  </div>
</template>

<script>
import { VText, DeviceIphone } from '@campsite-bio/component-lib';
import { useContext, computed } from '@nuxtjs/composition-api';
import shuffle from 'lodash/shuffle';
import { useWindowSize } from '@vueuse/core';

export default {
  components: {
    VText,
    DeviceIphone,
  },

  props: {
    subtitle: {
      type: String,
      default: null,
    },

    title: {
      type: String,
      required: true,
    },

    text: {
      type: String,
      required: true,
    },

    ctaText: {
      type: String,
      default: null,
    },

    profileImages: {
      type: Array,
      default: null,
    },
  },

  setup(props) {
    const { $img, isDev } = useContext();

    const { width } = useWindowSize();

    const profileImagesGenerated = computed(() => {
      return props.profileImages?.map((image) => {
        const src = image;
        return isDev
          ? src
          : $img(src, { width: 300, quality: 80, format: 'webp' });
      });
    });

    const usernamesToShow = computed(() => {
      if (!process.client) return profileImagesGenerated.value;

      const phoneWidth = 150;
      const numberToShow = Math.ceil(width.value / phoneWidth) + 1;

      return shuffle(profileImagesGenerated.value).slice(0, numberToShow);
    });

    const classes = computed(() => {
      return {
        'masthead--has-profiles': props.profileImages?.length > 0,
      };
    });

    return { usernamesToShow, classes, isClient: process.client };
  },
};
</script>

<style lang="scss" scoped>
.masthead {
  background: var(--g-color-gray-100);
  background-image: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 1)
    ),
    url('~assets/images/mark-pattern-white.svg');
  background-size: auto, 80px auto;

  text-align: center;

  .grid {
    padding-bottom: 5rem;
    padding-top: 3rem;
  }

  &--has-profiles {
    overflow: hidden;

    .grid {
      padding-bottom: 18rem;
    }
  }

  &__content {
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }

  &__cta__actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    justify-content: center;
  }

  &__profiles {
    display: flex;
    gap: 0.25rem;
    position: absolute;
    left: 50%;
    bottom: -4rem;
    transform: translateX(-50%);
  }

  &__profile {
    position: relative;
    height: 303px;
    width: 150px;
    flex-shrink: 0;
    transition: transform var(--g-transition-speed);

    &:hover {
      transform: translateY(-1.5rem);
    }

    &::v-deep .device-iphone-x {
      transform: scale(0.35, 0.35);
      transform-origin: top left;
      position: absolute;
    }
  }
}

.masthead__for.masthead__for {
  margin-bottom: 0.5rem;
}

.masthead__title.masthead__title {
  margin-bottom: 1rem;
  font-size: 2.5rem;

  @media (min-width: 768px) {
    font-size: 3rem;
  }
}

.masthead__text.masthead__text {
  margin-bottom: 2rem;
  font-size: 1.25rem;
}

.masthead__btn-text.masthead__btn-text {
  margin-top: 0.5rem;
  font-size: 0.85rem;
}

.previews-enter-active,
.previews-leave-active {
  transition: all var(--g-transition-speed);
}

.previews-enter,
.previews-leave-to {
  opacity: 0;
}
</style>
